import { BehaviorSubject } from "rxjs";

export class AudioPlayer {
    private audioContext: AudioContext;
    private chunks: ArrayBuffer[] = [];
    private isProcessing = false;
    private bufferSize = 20; // Number of chunks to collect before playing
    private currentSource: AudioBufferSourceNode | undefined = undefined;

    // Timer handle to flush the buffer if no new chunk arrives
    private flushTimeout: number | undefined;
      
    // Observable for playback state
    public isPlaying$ = new BehaviorSubject<boolean>(false);
  
    constructor() {
      this.audioContext = new AudioContext();
    }
  
    public async feedChunk(data: string) {
      try {
        const arrayBuffer = this.base64ToArrayBuffer(data);
        this.chunks.push(arrayBuffer);

        // Reset the flush timer on every new chunk
        if (this.flushTimeout) {
            clearTimeout(this.flushTimeout);
        }
        // Set a timer to flush after 500ms if no new chunk arrives
        this.flushTimeout = window.setTimeout(() => {
            if (!this.isProcessing && this.chunks.length > 0) {
            this.processChunks();
            }
        }, 500);
  
        if (this.chunks.length >= this.bufferSize && !this.isProcessing) {
          await this.processChunks();
        }
      } catch (error) {
        console.error('Error feeding chunk:', error);
      }
    }
  
    private async processChunks() {
      if (this.isProcessing || this.chunks.length === 0) return;
  
      try {
        this.isProcessing = true;
  
        // Combine all chunks into one buffer
        const totalLength = this.chunks.reduce((acc, chunk) => acc + chunk.byteLength, 0);
        const combinedBuffer = new Uint8Array(totalLength);
        
        let offset = 0;
        this.chunks.forEach(chunk => {
          combinedBuffer.set(new Uint8Array(chunk), offset);
          offset += chunk.byteLength;
        });
  
        // Clear chunks array
        this.chunks = [];
  
        // Decode and play
        const audioBuffer = await this.audioContext.decodeAudioData(combinedBuffer.buffer);
        const source = this.audioContext.createBufferSource();
        this.currentSource = source;
        source.buffer = audioBuffer;
        source.connect(this.audioContext.destination);

              // Notify that playback is starting
        this.isPlaying$.next(true);
        source.start(0);
  
        // When this buffer is done, process any remaining chunks
        source.onended = () => {
          this.currentSource = undefined;
          this.isPlaying$.next(false);
          this.isProcessing = false;
          if (this.chunks.length >= this.bufferSize) {
            this.processChunks();
          }
        };
      } catch (error) {
        console.error('Error processing chunks:', error);
        this.currentSource = undefined;
        this.isProcessing = false;
        this.isPlaying$.next(false);
      }
    }
  
    public stop() {
        // Stop the current audio source if playing
        if (this.currentSource) {
          this.currentSource.stop();
          this.currentSource = undefined;
        }
        // Clear any queued chunks and reset processing state
        this.chunks = [];
        this.isProcessing = false;
        // Notify that playback has stopped
        this.isPlaying$.next(false);
      }

    private base64ToArrayBuffer(base64: string): ArrayBuffer {
      const binaryString = window.atob(base64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    }

    public blobToBase64(blob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            // The result is in data URL format (e.g., "data:audio/webm;base64,AAA...")
            // We extract the base64 part by splitting at the comma.
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      }    
  } 